import type { FC, InputHTMLAttributes, ReactNode } from "react";
import { useId } from "react";
import clsx from "clsx";

import { ErrorMessage } from "@/components/Input.tsx";

interface Props extends InputHTMLAttributes<HTMLInputElement> {
	label: ReactNode;
	error?: string;
}

/**
 * The label text is a sibling of the <label>, not its content, so that it can carry its own
 * interactive elements — the AGB row puts two document links in the sentence, and inside a
 * <label> every click on one would also toggle the box. The empty <label> covers the visual
 * checkbox, and `aria-labelledby` points at the sentence, so the accessible name is still the
 * whole thing including the link text.
 *
 * The trade-off is that clicking the sentence no longer toggles the box; only the box does.
 */
export const Checkbox: FC<Props> = ({ label, error, className, id, ...props }) => {
	const generatedId = useId();
	const inputId = id ?? generatedId;
	const labelId = `${inputId}-label`;

	return (
		<div className="flex flex-col gap-1">
			{error && <ErrorMessage>{error}</ErrorMessage>}
			<div className="text-brand-dark-900 flex items-start gap-3 text-sm leading-5">
				<span className="mt-0.5 inline-flex h-4 w-4">
					<input
						id={inputId}
						type="checkbox"
						aria-labelledby={labelId}
						aria-invalid={error ? true : undefined}
						className="peer sr-only"
						{...props}
					/>
					{/* Must stay the input's immediate sibling — every peer-* utility below
					    resolves through the ~ combinator. */}
					<label
						htmlFor={inputId}
						className={clsx(
							"border-brand-dark/10 h-4 w-4 cursor-pointer rounded border",
							"peer-checked:border-brand-primary peer-checked:bg-brand-primary peer-checked:bg-[url('../assets/icons/checked.svg')] peer-checked:bg-size-[100%_100%] peer-checked:bg-center peer-checked:bg-no-repeat",
							"hover:border-brand-primary",
							"peer-focus-visible:ring-brand-primary peer-focus-visible:ring-2 peer-focus-visible:ring-offset-2",
							"transition-colors duration-150",

							error && "border-red-600",
							className
						)}
					/>
				</span>
				<span id={labelId}>{label}</span>
			</div>
		</div>
	);
};
